/**
 * Broken Logo - Spreadsheet Arena
 *
 * A fractured version of the main logo for 404/error states.
 * The spreadsheet grid appears intact, then elegantly fractures -
 * top portion shifts and tilts, revealing the break.
 *
 * The Intelligent Standard: Deliberate, sophisticated motion.
 * The animation tells a story - intact → fracture → settle.
 */

interface BrokenLogoProps {
  size?: number;
  variant?: 'default' | 'light' | 'dark';
  animate?: boolean;
}

export function BrokenLogo({
  size = 40,
  variant = 'light',
  animate = true,
}: BrokenLogoProps) {
  const color = {
    default: '#8B2332',
    light: '#FFFFFF',
    dark: '#1A1A1A',
  }[variant];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Page not found"
      style={{ overflow: 'visible' }}
    >
      {animate && (
        <style>{`
          @keyframes logoFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes topShift {
            0% {
              transform: translate(0, 0) rotate(0deg);
            }
            100% {
              transform: translate(2px, -1px) rotate(8deg);
            }
          }
          
          @keyframes subtleSettle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(0.5px); }
          }
          
          .broken-logo-container {
            animation: logoFadeIn 0.4s ease-out forwards;
          }
          
          .broken-top {
            transform-origin: 12px 11px;
            animation: topShift 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            animation-delay: 0.3s;
          }
          
          .broken-bottom {
            animation: subtleSettle 0.3s ease-out forwards;
            animation-delay: 0.5s;
          }
        `}</style>
      )}

      <g className={animate ? 'broken-logo-container' : undefined}>
        {/* Top half - shifts and tilts after initial reveal */}
        <g
          className={animate ? 'broken-top' : undefined}
          transform={animate ? undefined : 'translate(2, -1) rotate(8, 12, 11)'}
        >
          {/* Architrave */}
          <rect x="2" y="2" width="20" height="2.5" rx="0.5" fill={color} />

          {/* Top row cells */}
          <rect x="3" y="5" width="4.5" height="3" rx="0.25" fill={color} />
          <rect
            x="9.75"
            y="5"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.9"
          />
          <rect
            x="16.5"
            y="5"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.8"
          />

          {/* Second row */}
          <rect
            x="3"
            y="8.5"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.8"
          />
          <rect
            x="9.75"
            y="8.5"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.7"
          />
          <rect
            x="16.5"
            y="8.5"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.6"
          />
        </g>

        {/* Bottom half - stays grounded */}
        <g className={animate ? 'broken-bottom' : undefined}>
          {/* Third row cells */}
          <rect
            x="3"
            y="12"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.6"
          />
          <rect
            x="9.75"
            y="12"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.5"
          />
          <rect
            x="16.5"
            y="12"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.4"
          />

          {/* Fourth row cells */}
          <rect
            x="3"
            y="15.5"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.4"
          />
          <rect
            x="9.75"
            y="15.5"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.35"
          />
          <rect
            x="16.5"
            y="15.5"
            width="4.5"
            height="3"
            rx="0.25"
            fill={color}
            opacity="0.3"
          />

          {/* Base */}
          <rect x="2" y="19" width="20" height="2.5" rx="0.5" fill={color} />
        </g>
      </g>
    </svg>
  );
}
